/**
 * Tiers and Providers of "Season vs last year" (CU-869fmhmx8 E2, E3): who counts as the market. They act after the
 * load, on the rows already received, and never ask the server. Your brand is always shown and always counts; your
 * other brands only when picked or in the tier. Find a provider is not one of them: it only finds a row.
 *
 * Pure and alias-free: `node --test` runs it.
 */

/** The filter in use: the tier's providers, or the ones picked by hand; `null` = the whole market */
function marketFilter({ tierProviders = null, picked = [] }) {
	if (tierProviders) return new Set(tierProviders.map(Number))
	return picked?.length ? new Set(picked.map(Number)) : null
}

/** The ids shown (a Set). Without a filter, everyone; with one, its providers that have rows, plus your brand. */
export function visibleProviderIds({ providers = [], identity = null, tierProviders = null, picked = [] }) {
	const filter = marketFilter({ tierProviders, picked })
	const primary = identity ? Number(identity.primaryId) : null
	return new Set(providers.map(p => Number(p.id)).filter(id => !filter || filter.has(id) || id === primary))
}

export const marketOn = ({ tierProviders = null, picked = [] }) => !!marketFilter({ tierProviders, picked })

/** The tier's providers that have rows (your brand aside): `{ here, total, ids }` */
export function tierCoverage({ providers = [], identity = null, tierProviders = [] }) {
	const primary = identity ? Number(identity.primaryId) : null
	const present = new Set(providers.map(p => Number(p.id)).filter(id => id !== primary))
	const here = tierProviders.filter(id => present.has(Number(id)))
	return { here: here.length, total: tierProviders.length, ids: here.map(Number) }
}

/** The options of Providers (UX D3): the providers of the answer; your brand first and disabled, your other brands marked */
export function providerOptions({ providers = [], identity = null }) {
	const byName = (a, b) => String(a.label).localeCompare(String(b.label))
	const opts = providers.map(p => ({ value: Number(p.id), label: p.name, own: identity ? p.own || null : null }))
	const you = opts.filter(o => o.own === 'primary').map(o => ({ ...o, disabled: true, note: 'You · always shown' }))
	const rest = opts.filter(o => o.own !== 'primary').sort(byName).map(o => ({ ...o, disabled: false, note: o.own === 'other' ? 'Yours' : null }))
	return [...you, ...rest]
}

/**
 * The card subtitle with a market filter on (toolbar polish 2026-10-26, replaces the blue line and the line under
 * Tiers): the count once, the tier (with its coverage only when some of its providers have no prices here), and what
 * the figures are computed among. `action` clears the market filters.
 */
export function scopeSubtitle({ shown, all, competitors, identity, tierName = null, coverage = null }) {
	const who = `${shown} of ${all} ${identity ? 'other providers' : 'providers'} shown`
	const partial = coverage && coverage.here < coverage.total ? ` (${coverage.here} of its ${coverage.total} have prices here)` : ''
	const tier = tierName ? ` · ${tierName}${partial}` : ''
	const among = !competitors ? 'no market to compare with'
		: identity ? 'median, position and cheapest among those shown' : 'median and cheapest among those shown'
	return { text: `${who}${tier} · ${among}`, action: 'Show the whole market' }
}

/** The subtitles of the rows on top, with a market filter (`null` = the ones of the whole market) */
export function fixedRowSubs({ filtered, identity, competitors }) {
	if (!filtered) return null
	const median = identity
		? competitors === 0 ? 'No competitors shown' : competitors === 1 ? 'The only competitor shown' : `The ${competitors} competitors shown`
		: competitors === 0 ? 'No providers shown' : competitors === 1 ? 'The only provider shown' : `The ${competitors} providers shown`
	return { median, position: '1 = the cheapest of those shown', cheapest: 'of the providers shown' }
}

/** The row of the table when the filter leaves no competitor */
export const noCompetitorsText = tierName => (tierName
	? `None of the providers in “${tierName}” has prices for this view.`
	: 'None of the providers you chose has prices for this view.')

/** `market: Low cost` / `market: providers chosen` for the context line */
export const marketContext = tierName => (tierName ? `market: ${tierName}` : 'market: providers chosen')

/**
 * What the subtitle adds for the filters (E3): the median among the competitors shown (and the tier), and the cars.
 * `filters` = `{ tierName, carsText }`; `model.filtered` says whether Tiers / Providers are on.
 */
export function detailSubtitleTail({ isMedian, model, filters = {} }) {
	let tail = ''
	if (isMedian) {
		if (model.filtered) {
			const n = model.counts?.competitors ?? model.competitors.length
			tail += model.identity
				? ` · median of the ${n} competitor${n === 1 ? '' : 's'} shown`
				: ` · median of the ${n} provider${n === 1 ? '' : 's'} shown`
			if (filters.tierName) tail += ` (${filters.tierName})`
		} else {
			tail += model.identity ? ' · median of your competitors, your brands excluded' : ' · median of all providers'
		}
	}
	if (filters.carsText) tail += ` · ${filters.carsText}`
	return tail
}
